<template>
    <a
        v-if="href"
        v-bind:href="href"
        target="_blank"
        v-bind="$attrs"
    >
        <slot></slot>
    </a>
    <RouterLink
        v-else
        v-bind:to="routerLinkTo"
        v-bind="$attrs"
    >
        <slot></slot>
    </RouterLink>
</template>

<script>
    export default {
        /**
         * Determine how unrecognized component properties should be handled by the Vue compiler.
         *
         * @link https://vuejs.org/v2/api/#inheritAttrs
         *
         * @type {Boolean}
         */
        inheritAttrs: false,

        /**
         * Define the properties passed into the component from the parent.
         *
         * @link https://vuejs.org/v2/guide/components.html#Props
         */
        props: {
            href: {
                type: String,
                default: '',
            },
            allowInsecure: {
                type: Boolean,
                default: false,
            },
            to: {
                type: Object,
                default: null,
            },
            name: {
                type: String,
                default: '',
            },
            params: {
                type: Object,
                default: () => ({}),
            },
        },

        /**
         * Cached computed methods for complex logic on the view layer.
         *
         * @link https://vuejs.org/v2/guide/computed.html
         *
         * @type Object The computed functions
         */
        computed: {
            routerLinkTo ({ name, params }) {
                return {
                    name,
                    params,
                    ...(this.to || {}),
                };
            },
        },

        /**
         * This method logic will be run immediately after the Vue instance is created.
         *
         * @link https://vuejs.org/v2/guide/instance.html#Lifecycle-Diagram
         */
        created () {
            this.validateProps();
        },

        /**
         * Non-cached methods that are used by the Vue instance and template.
         *
         * @type Object ES6 methods
         */
        methods: {
            // Perform more complex prop validations than is possible
            // inside individual validator functions for each prop.
            validateProps () {
                if (process.env.NODE_ENV === 'production') {
                    return;
                }

                if (this.href) {
                    // Check for non-external URL in href.
                    if (!(/^\w+:/).test(this.href)) {
                        return console.warn(
                            `Invalid <AppLink> href: ${
                                this.href
                            }.\nIf you're trying to link to a local URL, provide at least a name or to`
                        );
                    }
                    // Check for insecure URL in href.
                    if (!this.allowInsecure && !(/^(https|mailto|tel):/).test(this.href)) {
                        return console.warn(
                            `Insecure <AppLink> href: ${
                                this.href
                            }.\nWhen linking to external sites, always prefer https URLs.`
                                + ` If this site does not offer SSL, explicitly add the allow-insecure attribute on <AppLink>.`
                        );
                    }
                } else if (!this.name && !this.to) {
                    // Check for insufficient props.
                    return console.warn(
                        `Invalid <AppLink> props:\n\n${JSON.stringify(
                            this.$props,
                            null,
                            2
                        )}\n\nEither a \`name\` or \`to\` is required for internal links, or an \`href\` for external links.`
                    );
                }
            },
        },
    };
</script>
